import { useCallback, useEffect, useState } from 'react';

import type { CFSReportContext } from '@/pages/CFS/reports/cfsReport/types';

import { CFSRedactionsMap, initCFSRedactions } from '../data';

const emptyMap = (): CFSRedactionsMap => ({});

const cloneEntry = (entry: {
  redactedKeys: Set<string>;
  redactedWords: Set<string>;
  redactedOccurrences: Set<string>;
}) => ({
  redactedKeys: new Set(entry.redactedKeys),
  redactedWords: new Set(entry.redactedWords),
  redactedOccurrences: new Set(entry.redactedOccurrences),
});

const cloneMap = (sourceMap: CFSRedactionsMap): CFSRedactionsMap => {
  const next: CFSRedactionsMap = {};
  for (const sectionKey of Object.keys(sourceMap)) {
    const sectionEntries = sourceMap[sectionKey];
    const nextSection: Record<number, ReturnType<typeof cloneEntry>> = {};
    for (const itemIndexKey of Object.keys(sectionEntries)) {
      const itemIndex = Number(itemIndexKey);
      nextSection[itemIndex] = cloneEntry(sectionEntries[itemIndex]);
    }
    next[sectionKey] = nextSection;
  }
  return next;
};

export const useCFSRedactions = (ctx: CFSReportContext | undefined) => {
  const [redactions, setRedactions] = useState<CFSRedactionsMap>(emptyMap);

  // Re-init whenever ctx becomes available or changes identity
  // (the modal fetches ctx once per open).
  useEffect(() => {
    if (ctx) setRedactions(initCFSRedactions(ctx));
  }, [ctx]);

  const ensureItemEntry = (
    next: CFSRedactionsMap,
    sectionKey: string,
    itemIndex: number,
  ) => {
    const sectionEntries = next[sectionKey] ?? (next[sectionKey] = {});
    const itemEntry =
      sectionEntries[itemIndex] ??
      (sectionEntries[itemIndex] = {
        redactedKeys: new Set<string>(),
        redactedWords: new Set<string>(),
        redactedOccurrences: new Set<string>(),
      });
    return itemEntry;
  };

  const toggleFieldRedaction = useCallback(
    (sectionKey: string, itemIndex: number, fieldKey: string) => {
      setRedactions((prev) => {
        const next = cloneMap(prev);
        const itemEntry = ensureItemEntry(next, sectionKey, itemIndex);
        if (itemEntry.redactedKeys.has(fieldKey)) {
          itemEntry.redactedKeys.delete(fieldKey);
        } else {
          itemEntry.redactedKeys.add(fieldKey);
        }
        return next;
      });
    },
    [],
  );

  const addWordRedaction = useCallback(
    (
      sectionKey: string,
      itemIndex: number,
      _fieldKey: string,
      word: string,
    ) => {
      setRedactions((prev) => {
        const next = cloneMap(prev);
        const itemEntry = ensureItemEntry(next, sectionKey, itemIndex);
        itemEntry.redactedWords.add(word);
        return next;
      });
    },
    [],
  );

  const removeWordRedaction = useCallback(
    (
      sectionKey: string,
      itemIndex: number,
      _fieldKey: string,
      word: string,
    ) => {
      setRedactions((prev) => {
        const next = cloneMap(prev);
        const itemEntry = next[sectionKey]?.[itemIndex];
        if (itemEntry) itemEntry.redactedWords.delete(word);
        return next;
      });
    },
    [],
  );

  const addOccurrenceRedactions = useCallback(
    (sectionKey: string, itemIndex: number, occurrenceIds: string[]) => {
      if (!occurrenceIds.length) return;
      setRedactions((prev) => {
        const next = cloneMap(prev);
        const itemEntry = ensureItemEntry(next, sectionKey, itemIndex);
        occurrenceIds.forEach((occurrenceId) =>
          itemEntry.redactedOccurrences.add(occurrenceId),
        );
        return next;
      });
    },
    [],
  );

  const removeOccurrenceRedactions = useCallback(
    (sectionKey: string, itemIndex: number, occurrenceIds: string[]) => {
      if (!occurrenceIds.length) return;
      setRedactions((prev) => {
        const next = cloneMap(prev);
        const itemEntry = next[sectionKey]?.[itemIndex];
        if (!itemEntry) return prev;
        const cloned = cloneEntry(itemEntry);
        occurrenceIds.forEach((occurrenceId) =>
          cloned.redactedOccurrences.delete(occurrenceId),
        );
        next[sectionKey][itemIndex] = cloned;
        return next;
      });
    },
    [],
  );

  const clearAllWordRedactions = useCallback(() => {
    setRedactions((prev) => {
      const next = cloneMap(prev);
      for (const sectionKey of Object.keys(next)) {
        for (const itemIndexKey of Object.keys(next[sectionKey])) {
          const itemIndex = Number(itemIndexKey);
          next[sectionKey][itemIndex].redactedWords = new Set<string>();
          next[sectionKey][itemIndex].redactedOccurrences = new Set<string>();
        }
      }
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setRedactions(ctx ? initCFSRedactions(ctx) : emptyMap());
  }, [ctx]);

  return {
    redactions,
    toggleFieldRedaction,
    addWordRedaction,
    removeWordRedaction,
    addOccurrenceRedactions,
    removeOccurrenceRedactions,
    clearAllWordRedactions,
    resetAll,
  };
};
